import { observable, action, computed } from "mobx"
import axios from "axios"
import _ from "lodash"
import { createCollectionApiUrl } from "../utils/samplefocusDefaults"
import { getCsrfToken } from "../utils/userUtils"
import Sample from "./sample"

export default class Collection {
  @observable name
  @observable touchedFields = {}
  @observable samples = []
  @observable isPersisted
  @observable imageUrl
  @observable imageThumbUrl
  @observable allSamplesPublished

  constructor(collectionObject) {
    const o = collectionObject
    this.csrfToken = getCsrfToken()
    this.id = o.id
    this.name = o.name ? o.name : ""
    this.imageUrl = o.image_url
    this.imageThumbUrl = o.image_thumb_url
    this.isPersisted = o.created_at ? true : false
    this.apiCollectionUrl = o.api_collection_url
    this.allSamplesPublished = o.all_samples_published
    this.samplesToPreview = o.samples_to_preview ?  o.samples_to_preview.map((sample) => new Sample(sample)) : []
    this.samplesPublishedCount = o.samples_published_count
    this.collectionUrl = o.collection_url
    this.samples = o.samples ? o.samples : []
    this.slug = o.slug
  }

  @action
  touchField = (field) => {
    this.touchedFields[field] = true
  }

  @action
  touchAllFields = () => {
    const fieldsToTouch = ["name", "image"]
    fieldsToTouch.forEach((fieldName) => {
      this.touchField(fieldName)
    })
  }

  @action
  setIfPublished = (shouldBePublished) => {
    this.allSamplesPublished = shouldBePublished
  }

  @computed
  get validationErrors() {
    const errors = {}
    if (_.isEmpty(this.name)) {
      errors["name"] = true
    }

    // Only allow collections with a unique user's samples
    const userSlugs = this.samples.map((s) => s.user_slug)
    if(_.uniq(userSlugs).length > 1) {
      errors["multipleUsers"] = true
    }

    return errors
  }

  @computed
  get sampleIds() {
    return this.samples.map((s) => s.id)
  }

  async create(options = {}) {
    const errors = this.validationErrors
    if (!_.isEmpty(errors)) {
      console.log("Error on save", errors)
      return false
    }

    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    let collectionData = new FormData()
    collectionData.append("collection[name]", this.name)
    this.sampleIds.forEach((sampleId) => {
      // Need to append this way for FormData not to stringify the sampleIds array
      collectionData.append("collection[sample_ids][]", sampleId)
    })
    if (this.newImageFile) {
      collectionData.append("collection[image]", this.newImageFile)
    }

    if (options.setToSampleOwner) {
      collectionData.append("set_to_sample_owner", options.setToSampleOwner)
    }

    const response = await axios({
      method: "post",
      url: createCollectionApiUrl,
      timeout: 25000,
      data: collectionData,
    })
    console.log("response", response)
    this.isPersisted = true

    if (this.newImageFile) {
      this.imageUrl = URL.createObjectURL(this.newImageFile)
      this.imageThumbUrl = URL.createObjectURL(this.newImageFile)
    }
    return response
  }

  async update() {
    const errors = this.validationErrors
    if (!_.isEmpty(errors)) {
      console.log("Error on save", errors)
      return false
    }

    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    let collectionData = new FormData()
    collectionData.append("collection[name]", this.name)
    collectionData.append("collection[publish_all_samples]", this.allSamplesPublished)
    if (this.newImageFile) {
      collectionData.append("collection[image]", this.newImageFile)
    }

    const response = await axios({
      method: "patch",
      url: this.apiCollectionUrl,
      timeout: 25000,
      data: collectionData,
    })

    console.log("response", response)
    this.isPersisted = true
    if (this.newImageFile) {
      this.imageUrl = URL.createObjectURL(this.newImageFile)
      this.imageThumbUrl = URL.createObjectURL(this.newImageFile)
    }
    return response
  }

  async delete() {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "delete",
        url: this.apiCollectionUrl,
        timeout: 25000,
      })

      console.log("response", response)
      this.isPersisted = false

      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }

  async assignSamples(sampleIds) {
    const errors = this.validationErrors
    if (!_.isEmpty(errors)) {
      console.log("Error on save", errors)
      return false
    }

    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      let collectionData = new FormData()
      sampleIds.forEach(id => collectionData.append("collection[sample_ids][]", id))
      const assignSamplesUrl = this.apiCollectionUrl + "/assign_samples"
      const response = await axios({
        method: "post",
        url: assignSamplesUrl,
        timeout: 25000,
        data: collectionData,
      })

      console.log("response", response)
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }


  async removeSample(sampleSlug) {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const removeSampleUrl = this.apiCollectionUrl + "/remove_sample/" + sampleSlug
      const response = await axios({
        method: "put",
        url: removeSampleUrl,
        timeout: 25000,
      })

      console.log("response", response)
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }
}
